/**
 * Canadian tax brackets and registered-account limits (2026).
 * Sources: CRA (canada.ca/taxes), Revenu Québec (revenuquebec.ca).
 */

export const TAX_DATA = {
  federal: [
    { min: 0, max: 58523, rate: 0.14 },
    { min: 58523, max: 117045, rate: 0.205 },
    { min: 117045, max: 181440, rate: 0.26 },
    { min: 181440, max: 258482, rate: 0.29 },
    { min: 258482, max: Infinity, rate: 0.33 },
  ],

  provinces: {
    QC: {
      name: 'Québec',
      abatement: 0.165,
      brackets: [
        { min: 0, max: 54345, rate: 0.14 },
        { min: 54345, max: 108680, rate: 0.19 },
        { min: 108680, max: 132245, rate: 0.24 },
        { min: 132245, max: Infinity, rate: 0.2575 },
      ],
    },
    ON: {
      name: 'Ontario',
      abatement: 0,
      brackets: [
        { min: 0, max: 53891, rate: 0.0505 },
        { min: 53891, max: 107785, rate: 0.0915 },
        { min: 107785, max: 150000, rate: 0.1116 },
        { min: 150000, max: 220000, rate: 0.1216 },
        { min: 220000, max: Infinity, rate: 0.1316 },
      ],
    },
    BC: {
      name: 'British Columbia',
      abatement: 0,
      brackets: [
        { min: 0, max: 50363, rate: 0.056 },
        { min: 50363, max: 100728, rate: 0.077 },
        { min: 100728, max: 115648, rate: 0.105 },
        { min: 115648, max: 140430, rate: 0.1229 },
        { min: 140430, max: 190405, rate: 0.147 },
        { min: 190405, max: 265545, rate: 0.168 },
        { min: 265545, max: Infinity, rate: 0.205 },
      ],
    },
    AB: {
      name: 'Alberta',
      abatement: 0,
      brackets: [
        { min: 0, max: 61200, rate: 0.08 },
        { min: 61200, max: 154259, rate: 0.10 },
        { min: 154259, max: 185111, rate: 0.12 },
        { min: 185111, max: 246813, rate: 0.13 },
        { min: 246813, max: 370220, rate: 0.14 },
        { min: 370220, max: Infinity, rate: 0.15 },
      ],
    },
    MB: {
      name: 'Manitoba',
      abatement: 0,
      brackets: [
        { min: 0, max: 47000, rate: 0.108 },
        { min: 47000, max: 100000, rate: 0.1275 },
        { min: 100000, max: Infinity, rate: 0.174 },
      ],
    },
    SK: {
      name: 'Saskatchewan',
      abatement: 0,
      brackets: [
        { min: 0, max: 54532, rate: 0.105 },
        { min: 54532, max: 155805, rate: 0.125 },
        { min: 155805, max: Infinity, rate: 0.145 },
      ],
    },
    NS: {
      name: 'Nova Scotia',
      abatement: 0,
      brackets: [
        { min: 0, max: 30995, rate: 0.0879 },
        { min: 30995, max: 61991, rate: 0.1495 },
        { min: 61991, max: 97417, rate: 0.1667 },
        { min: 97417, max: 157124, rate: 0.175 },
        { min: 157124, max: Infinity, rate: 0.21 },
      ],
    },
    NB: {
      name: 'New Brunswick',
      abatement: 0,
      brackets: [
        { min: 0, max: 52333, rate: 0.094 },
        { min: 52333, max: 104666, rate: 0.14 },
        { min: 104666, max: 193861, rate: 0.16 },
        { min: 193861, max: Infinity, rate: 0.195 },
      ],
    },
    PE: {
      name: 'Prince Edward Island',
      abatement: 0,
      brackets: [
        { min: 0, max: 33928, rate: 0.095 },
        { min: 33928, max: 65820, rate: 0.1347 },
        { min: 65820, max: 106890, rate: 0.166 },
        { min: 106890, max: 142250, rate: 0.1762 },
        { min: 142250, max: Infinity, rate: 0.19 },
      ],
    },
    NL: {
      name: 'Newfoundland & Labrador',
      abatement: 0,
      brackets: [
        { min: 0, max: 44678, rate: 0.087 },
        { min: 44678, max: 89354, rate: 0.145 },
        { min: 89354, max: 159528, rate: 0.158 },
        { min: 159528, max: 223340, rate: 0.178 },
        { min: 223340, max: 285319, rate: 0.198 },
        { min: 285319, max: 570638, rate: 0.208 },
        { min: 570638, max: 1141275, rate: 0.213 },
        { min: 1141275, max: Infinity, rate: 0.218 },
      ],
    },
  },

  tfsaLimits: {
    2009: 5000, 2010: 5000, 2011: 5000, 2012: 5000,
    2013: 5500, 2014: 5500, 2015: 10000, 2016: 5500,
    2017: 5500, 2018: 5500, 2019: 6000, 2020: 6000,
    2021: 6000, 2022: 6000, 2023: 6500, 2024: 7000,
    2025: 7000, 2026: 7000,
  },

  rrspMaxDeduction: 33810,
  rrspRate: 0.18,

  fhsaAnnual: 8000,
  fhsaLifetime: 40000,
};

/** Province list for selectors (sorted by Latino population) */
export const PROVINCES = [
  { value: 'QC', label: 'Québec' },
  { value: 'ON', label: 'Ontario' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'AB', label: 'Alberta' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'NL', label: 'Newfoundland & Labrador' },
];

/** Combined federal + provincial marginal rate */
export const getMarginalRate = (income, province) => {
  const provData = TAX_DATA.provinces[province];
  if (!provData) return 0.30;

  let fedRate = 0;
  for (const bracket of TAX_DATA.federal) {
    if (income > bracket.min) fedRate = bracket.rate;
  }
  fedRate *= (1 - provData.abatement);

  let provRate = 0;
  for (const bracket of provData.brackets) {
    if (income > bracket.min) provRate = bracket.rate;
  }

  return fedRate + provRate;
};

/** Accumulated TFSA contribution room since year of residency */
export const calcTFSARoom = (residencyYear) => {
  const currentYear = 2026;
  if (!residencyYear || residencyYear > currentYear) return 0;
  let room = 0;
  for (let year = Math.max(residencyYear, 2009); year <= currentYear; year++) {
    room += TAX_DATA.tfsaLimits[year] || 7000;
  }
  return room;
};

/** Simplified RRSP deduction limit (real limit is on NOA) */
export const calcRRSPRoom = (previousYearIncome) => {
  if (!previousYearIncome || previousYearIncome <= 0) return 0;
  return Math.min(previousYearIncome * TAX_DATA.rrspRate, TAX_DATA.rrspMaxDeduction);
};

/** Future value of monthly contributions with compound growth */
export const projectGrowth = (monthlyContribution, years, annualReturn) => {
  if (!monthlyContribution || monthlyContribution <= 0) return 0;
  const monthlyReturn = annualReturn / 12;
  const months = years * 12;
  if (monthlyReturn === 0) return monthlyContribution * months;
  return Math.round(
    monthlyContribution * ((Math.pow(1 + monthlyReturn, months) - 1) / monthlyReturn)
  );
};

/** RRSP after-tax value estimate at withdrawal */
export const rrspAfterTax = (projection, marginalRate) => {
  return Math.round(projection * (1 - marginalRate));
};

/**
 * Priority recommendation engine.
 * Returns up to 3 ordered account recommendations.
 */
export const getPriority = ({ income, province, objective, employerMatch, yearsInCanada, hasEmergencyFund, previousIncome }, t) => {
  const marginalRate = getMarginalRate(income || 0, province);
  const tfsaRoom = calcTFSARoom(yearsInCanada);

  // RRSP room depends on whether user had Canadian income last year
  let rrspRoom;
  let rrspNote = '';
  if (previousIncome === 'no') {
    rrspRoom = 0;
    rrspNote = t('calc.rrsp.noRoom');
  } else if (previousIncome === 'unknown') {
    rrspRoom = null;
    rrspNote = t('calc.rrsp.unknownRoom');
  } else {
    rrspRoom = calcRRSPRoom(income || 0);
    rrspNote = t('calc.rrsp.estimatedRoom');
  }

  const effectiveRrspRoom = rrspRoom || 0;
  const priority = [];

  // Rule 0: Employer match ALWAYS first (only if they have room)
  if (employerMatch === 'yes' && effectiveRrspRoom > 0) {
    priority.push({
      account: 'RRSP',
      badge: 'match',
      amount: effectiveRrspRoom,
      taxSaving: effectiveRrspRoom * marginalRate,
      reason: t('calc.logic.matchFirst'),
    });
  }

  // Rule 1: No emergency fund → TFSA first for liquidity
  if (hasEmergencyFund === 'no' && !priority.find(p => p.account === 'TFSA')) {
    priority.push({
      account: 'TFSA',
      amount: tfsaRoom,
      taxSaving: 0,
      reason: t('calc.logic.emergencyFirst'),
    });
  }

  // Rule 2: Home goal → FHSA
  if (objective === 'home') {
    priority.push({
      account: 'FHSA',
      amount: TAX_DATA.fhsaAnnual,
      taxSaving: TAX_DATA.fhsaAnnual * marginalRate,
      reason: t('calc.logic.fhsaHome'),
    });
  }

  // Rule 3: RRSP vs TFSA based on marginal rate
  if (marginalRate > 0.30 && effectiveRrspRoom > 0) {
    if (!priority.find(p => p.account === 'RRSP')) {
      priority.push({ account: 'RRSP', amount: effectiveRrspRoom, taxSaving: effectiveRrspRoom * marginalRate, reason: t('calc.logic.rrspHighIncome') });
    }
    if (!priority.find(p => p.account === 'TFSA')) {
      priority.push({ account: 'TFSA', amount: tfsaRoom, taxSaving: 0, reason: t('calc.logic.tfsaAfterRrsp') });
    }
  } else {
    if (!priority.find(p => p.account === 'TFSA')) {
      priority.push({ account: 'TFSA', amount: tfsaRoom, taxSaving: 0, reason: t('calc.logic.tfsaLowIncome') });
    }
    if (!priority.find(p => p.account === 'RRSP')) {
      priority.push({ account: 'RRSP', amount: effectiveRrspRoom, taxSaving: effectiveRrspRoom * marginalRate, reason: effectiveRrspRoom > 0 ? t('calc.logic.rrspAfterTfsa') : rrspNote });
    }
  }

  // Rule 4: FHSA as "consider" if not home goal
  if (objective !== 'home' && !priority.find(p => p.account === 'FHSA')) {
    priority.push({
      account: 'FHSA',
      amount: TAX_DATA.fhsaAnnual,
      taxSaving: TAX_DATA.fhsaAnnual * marginalRate,
      reason: t('calc.logic.fhsaConsider'),
    });
  }

  return { priority: priority.slice(0, 3), marginalRate, tfsaRoom, rrspRoom: effectiveRrspRoom, rrspRoomRaw: rrspRoom, rrspNote };
};
